import { supabase } from "@/integrations/supabase/client";

// ----- Types -----
export type Rarity = "common" | "rare" | "epic" | "legendary";

export type Achievement = {
  id: string;
  slug: string;
  name: string;
  description: string;
  category: string;
  trigger_type: string;
  trigger_params: Record<string, any>;
  rarity: Rarity;
  colour_hex: string;
  phase: number;
  is_active: boolean;
};

export type UserAchievement = {
  id: string;
  user_id: string;
  achievement_id: string;
  earned_at: string;
  trigger_visit_id: string | null;
  is_showcased: boolean;
  achievement: Achievement;
};

const RARITY_WEIGHT: Record<Rarity, number> = {
  legendary: 4,
  epic: 3,
  rare: 2,
  common: 1,
};

// Calendar streak: count consecutive days (today or yesterday-anchored) with at least one visit.
function calcStreak(isoDates: string[]): number {
  if (isoDates.length === 0) return 0;
  const days = new Set<string>();
  for (const iso of isoDates) {
    const d = new Date(iso);
    const key = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
    days.add(key);
  }
  // Anchor from today; if no visit today, allow yesterday
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const keyOf = (d: Date) => `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
  let cursor = new Date(today);
  if (!days.has(keyOf(cursor))) {
    cursor.setDate(cursor.getDate() - 1);
    if (!days.has(keyOf(cursor))) return 0;
  }
  let streak = 0;
  while (days.has(keyOf(cursor))) {
    streak += 1;
    cursor.setDate(cursor.getDate() - 1);
  }
  return streak;
}

export async function checkAchievements(
  visitId: string,
  userId: string,
): Promise<UserAchievement[]> {
  // ---------------- Step 1: load context ----------------
  const [
    visitRes,
    historyRes,
    achievementsRes,
    earnedRes,
    pendingRes,
    profileRes,
    parentCuisineCountRes,
  ] = await Promise.all([
    supabase
      .from("visits")
      .select(
        "id, restaurant_id, confirmed_at, restaurant:restaurants(id, visit_count, neighbourhood_id, city_id, price_tier, restaurant_cuisine_types(cuisine_type_id))",
      )
      .eq("id", visitId)
      .maybeSingle(),
    supabase
      .from("visits")
      .select(
        "id, restaurant_id, confirmed_at, restaurant:restaurants(id, neighbourhood_id, city_id, price_tier, restaurant_cuisine_types(cuisine_type_id))",
      )
      .eq("user_id", userId),
    supabase
      .from("achievements")
      .select("*")
      .eq("is_active", true)
      .eq("phase", 1),
    supabase
      .from("user_achievements")
      .select("achievement_id")
      .eq("user_id", userId),
    supabase
      .from("pending_achievements")
      .select("achievement_id")
      .eq("user_id", userId),
    supabase
      .from("profiles")
      .select("total_visits")
      .eq("id", userId)
      .maybeSingle(),
    supabase
      .from("cuisine_types")
      .select("id", { count: "exact", head: true })
      .is("parent_id", null)
      .eq("is_active", true),
  ]);

  const visit = visitRes.data as any;
  if (!visit) return [];

  const history = (historyRes.data ?? []) as any[];
  const allAchievements = (achievementsRes.data ?? []) as Achievement[];
  const earnedIds = new Set((earnedRes.data ?? []).map((r: any) => r.achievement_id));
  const pendingIds = new Set((pendingRes.data ?? []).map((r: any) => r.achievement_id));
  const totalVisits = profileRes.data?.total_visits ?? history.length;
  const totalParentCuisines = parentCuisineCountRes.count ?? 10;

  // Build cuisine slug + parent lookup
  const allCuisineIds = new Set<string>();
  for (const v of history) {
    const rels = v.restaurant?.restaurant_cuisine_types ?? [];
    for (const rel of rels) allCuisineIds.add(rel.cuisine_type_id);
  }
  const cuisineLookup: Record<string, { slug: string; parent_id: string | null }> = {};
  if (allCuisineIds.size > 0) {
    const { data: ct } = await supabase
      .from("cuisine_types")
      .select("id, slug, parent_id")
      .in("id", Array.from(allCuisineIds));
    for (const c of ct ?? []) {
      cuisineLookup[c.id] = { slug: c.slug, parent_id: c.parent_id };
    }
  }

  // Aggregates
  const visitsPerCuisineSlug: Record<string, number> = {};
  const distinctParents = new Set<string>();
  const visitsPerNeighbourhood: Record<string, number> = {};
  const distinctNeighbourhoods = new Set<string>();
  const visitsPerPriceTier: Record<number, number> = {};
  const dates: string[] = [];
  let visitsToThisRestaurant = 0;
  let afterHourCount = 0;

  for (const v of history) {
    dates.push(v.confirmed_at);
    if (v.restaurant_id === visit.restaurant_id) visitsToThisRestaurant += 1;

    const nh = v.restaurant?.neighbourhood_id;
    if (nh) {
      visitsPerNeighbourhood[nh] = (visitsPerNeighbourhood[nh] ?? 0) + 1;
      distinctNeighbourhoods.add(nh);
    }
    const pt = v.restaurant?.price_tier;
    if (pt) visitsPerPriceTier[pt] = (visitsPerPriceTier[pt] ?? 0) + 1;

    const rels = v.restaurant?.restaurant_cuisine_types ?? [];
    const seenSlugsThisVisit = new Set<string>();
    for (const rel of rels) {
      const meta = cuisineLookup[rel.cuisine_type_id];
      if (!meta) continue;
      if (!seenSlugsThisVisit.has(meta.slug)) {
        visitsPerCuisineSlug[meta.slug] = (visitsPerCuisineSlug[meta.slug] ?? 0) + 1;
        seenSlugsThisVisit.add(meta.slug);
      }
      if (meta.parent_id) distinctParents.add(meta.parent_id);
      else distinctParents.add(rel.cuisine_type_id);
    }

    const hour = new Date(v.confirmed_at).getHours();
    if (hour >= 21) afterHourCount += 1;
  }

  const currentStreak = calcStreak(dates);
  const restaurantVisitCount = visit.restaurant?.visit_count ?? 0; // already incremented by trigger

  // ---------------- DEBUG LOGGING ----------------
  const currentVisitCuisineIds = (visit.restaurant?.restaurant_cuisine_types ?? []).map(
    (r: any) => r.cuisine_type_id,
  );
  const currentVisitCuisineSlugs = currentVisitCuisineIds
    .map((id: string) => cuisineLookup[id]?.slug)
    .filter(Boolean);
  console.log("[achievementEngine] === checkAchievements ===");
  console.log("[achievementEngine] visitId:", visitId);
  console.log("[achievementEngine] restaurant_id:", visit.restaurant_id);
  console.log("[achievementEngine] current visit cuisine_type_ids:", currentVisitCuisineIds);
  console.log("[achievementEngine] current visit cuisine slugs:", currentVisitCuisineSlugs);
  console.log("[achievementEngine] visitsPerCuisineSlug (aggregate across history):", visitsPerCuisineSlug);

  // ---------------- Step 3: evaluate rules ----------------
  const passes = (a: Achievement, currentVisitCuisineSlugs: string[]): boolean => {
    const p = a.trigger_params ?? {};
    switch (a.trigger_type) {
      case "visit_count": {
        if (p.price_tier) {
          return (visitsPerPriceTier[p.price_tier] ?? 0) >= (p.count ?? 1);
        }
        if (p.after_hour !== undefined) {
          return afterHourCount >= (p.count ?? 1);
        }
        return totalVisits >= (p.total_visits ?? 1);
      }
      case "unique_cuisine": {
        if (p.cuisine_slug) {
          const currentVisitHasCuisine = currentVisitCuisineSlugs.includes(p.cuisine_slug);
          const requiredCount = p.count ?? 1;
          if (requiredCount === 1) {
            // First Bite: fires only on the very first visit to this cuisine.
            const isFirstVisit = (visitsPerCuisineSlug[p.cuisine_slug] ?? 0) === 1;
            return isFirstVisit && currentVisitHasCuisine;
          }
          // Threshold badge (e.g. 25 Japanese visits): fires on the visit that reaches the count.
          const visitsForCuisine = visitsPerCuisineSlug[p.cuisine_slug] ?? 0;
          return visitsForCuisine >= requiredCount && currentVisitHasCuisine;
        }
        if (p.distinct_parents) {
          if (p.require_all) return distinctParents.size >= totalParentCuisines;
          return distinctParents.size >= p.distinct_parents;
        }
        if (p.count) {
          return Object.values(visitsPerCuisineSlug).some((n) => n >= p.count);
        }
        return false;
      }
      case "unique_neighbourhood": {
        if (p.distinct_neighbourhoods) {
          return distinctNeighbourhoods.size >= p.distinct_neighbourhoods;
        }
        if (p.count) {
          return Object.values(visitsPerNeighbourhood).some((n) => n >= p.count);
        }
        return false;
      }
      case "streak_days":
        return currentStreak >= (p.streak ?? 1);
      case "first_visitor":
        return restaurantVisitCount === 1;
      case "low_visit_restaurant":
        return restaurantVisitCount <= (p.max_visit_count ?? 10);
      case "social_action":
        return false; // handled elsewhere
      default:
        return false;
    }
  };

  // DEBUG: log First Bite badge evaluation
  const firstBiteBadges = allAchievements.filter(
    (a) => a.trigger_type === "unique_cuisine" && a.slug?.startsWith("first-bite"),
  );
  console.log(
    "[achievementEngine] First Bite badges being evaluated:",
    firstBiteBadges.map((a) => ({
      slug: a.slug,
      name: a.name,
      cuisine_slug: a.trigger_params?.cuisine_slug,
      count: a.trigger_params?.count,
      alreadyEarned: earnedIds.has(a.id),
      isPending: pendingIds.has(a.id),
      passes: passes(a, currentVisitCuisineSlugs),
    })),
  );

  // Candidates: newly passing + previously pending
  // Special case: First Bite badges (unique_cuisine with cuisine_slug + count=1)
  // must NEVER be carried forward from pending. They only fire on the exact first
  // visit to that cuisine; if pending and not passing now, purge from pending.
  const stalePendingFirstBiteIds: string[] = [];
  const candidates: Achievement[] = [];
  for (const a of allAchievements) {
    if (earnedIds.has(a.id)) continue;
    const p = a.trigger_params ?? {};
    const isFirstBite =
      a.trigger_type === "unique_cuisine" && p.cuisine_slug && (p.count ?? 1) === 1;
    const nowPasses = passes(a, currentVisitCuisineSlugs);
    if (isFirstBite) {
      if (nowPasses) {
        candidates.push(a);
      } else if (pendingIds.has(a.id)) {
        stalePendingFirstBiteIds.push(a.id);
      }
      continue;
    }
    if (pendingIds.has(a.id) || nowPasses) candidates.push(a);
  }

  if (stalePendingFirstBiteIds.length > 0) {
    console.log(
      "[achievementEngine] Purging stale pending First Bite badges:",
      stalePendingFirstBiteIds,
    );
    await supabase
      .from("pending_achievements")
      .delete()
      .eq("user_id", userId)
      .in("achievement_id", stalePendingFirstBiteIds);
  }

  console.log(
    "[achievementEngine] Candidates passing trigger check:",
    candidates.map((c) => ({ slug: c.slug, trigger: c.trigger_type, params: c.trigger_params })),
  );

  // Rule C: only highest streak tier
  const streakBadges = candidates.filter((a) => a.trigger_type === "streak_days");
  const otherBadges = candidates.filter((a) => a.trigger_type !== "streak_days");
  let bestStreak: Achievement | null = null;
  for (const s of streakBadges) {
    if (!bestStreak || (s.trigger_params.streak ?? 0) > (bestStreak.trigger_params.streak ?? 0)) {
      bestStreak = s;
    }
  }

  // Rule D: max 1 per category — keep highest rarity per category
  const byCategory: Record<string, Achievement> = {};
  for (const a of otherBadges) {
    const existing = byCategory[a.category];
    if (!existing || RARITY_WEIGHT[a.rarity] > RARITY_WEIGHT[existing.rarity]) {
      byCategory[a.category] = a;
    }
  }
  const dedup = Object.values(byCategory);
  if (bestStreak) dedup.push(bestStreak);

  // Rule A: per-visit cap of 3 (sort by rarity desc)
  dedup.sort((a, b) => RARITY_WEIGHT[b.rarity] - RARITY_WEIGHT[a.rarity]);
  const awarded = dedup.slice(0, 3);
  const requeue = dedup.slice(3);

  // ---------------- Step 5: write ----------------
  const newlyEarned: UserAchievement[] = [];
  if (awarded.length > 0) {
    const rows = awarded.map((a) => ({
      user_id: userId,
      achievement_id: a.id,
      trigger_visit_id: visitId,
    }));
    const { data: inserted } = await supabase
      .from("user_achievements")
      .insert(rows)
      .select("*");
    for (const r of inserted ?? []) {
      const a = awarded.find((x) => x.id === r.achievement_id)!;
      newlyEarned.push({ ...(r as any), achievement: a });
    }
    // Remove from pending if applicable
    const ids = awarded.map((a) => a.id);
    await supabase
      .from("pending_achievements")
      .delete()
      .eq("user_id", userId)
      .in("achievement_id", ids);
  }

  // Re-queue overflow
  if (requeue.length > 0) {
    const rows = requeue
      .filter((a) => !pendingIds.has(a.id))
      .map((a) => ({ user_id: userId, achievement_id: a.id }));
    if (rows.length > 0) {
      await supabase.from("pending_achievements").insert(rows);
    }
  }

  return newlyEarned;
}

// Used after a feed post is created — awards "first-post" badge.
export async function checkFirstPostAchievement(userId: string): Promise<UserAchievement[]> {
  const [postCountRes, achievementRes, earnedRes] = await Promise.all([
    supabase
      .from("feed_posts")
      .select("id", { count: "exact", head: true })
      .eq("user_id", userId),
    supabase.from("achievements").select("*").eq("slug", "first-post").maybeSingle(),
    supabase
      .from("user_achievements")
      .select("achievement_id")
      .eq("user_id", userId),
  ]);
  const achievement = achievementRes.data as Achievement | null;
  if (!achievement) return [];
  const earnedIds = new Set((earnedRes.data ?? []).map((r: any) => r.achievement_id));
  if (earnedIds.has(achievement.id)) return [];
  if ((postCountRes.count ?? 0) < 1) return [];
  const { data: inserted } = await supabase
    .from("user_achievements")
    .insert({ user_id: userId, achievement_id: achievement.id })
    .select("*")
    .maybeSingle();
  if (!inserted) return [];
  return [{ ...(inserted as any), achievement }];
}

export function categoryEmoji(category: string): string {
  switch (category) {
    case "cuisine": return "🍜";
    case "neighbourhood": return "📍";
    case "streak": return "🔥";
    case "discovery": return "🔍";
    case "social": return "👥";
    case "dish": return "🍽️";
    case "city": return "🏙️";
    case "special": return "✨";
    default: return "🏅";
  }
}

export function rarityColour(rarity: Rarity): string {
  switch (rarity) {
    case "common": return "#10B981";
    case "rare": return "#3B82F6";
    case "epic": return "#8B5CF6";
    case "legendary": return "#F59E0B";
  }
}
